import React from "react";
import FlightIcon from "@mui/icons-material/Flight";
import type { SavedScenario } from "./RouteOptimizationTabs";
import { TRANSPORT_META } from "./transportTheme";

export const WL_RED = "#EE2D3D";
export const TEXT_SECONDARY = "#555";

/** Map optimization dropdown value to user-facing label */
const OPTIMIZATION_MODE_LABELS: Record<string, string> = {
  cost: "Cost",
  time: "Time",
  distance: "Distance",
  risk: "Risk",
};

export interface LocationNode {
  node_name: string;
  country: string;
  region?: string;
}

export interface FeasibleSupplier {
  supplier_name: string;
  supplier_min_qty?: number;
  supplier_capacity: number;
}

type FreightMode = "air" | "truck" | "rail" | "sea";

export type RouteCardViewModel = {
  routePath: string;
  riskPct: number;
  riskColor: string;
  modeLabel: string;
  totalCost: string;
  timeLabel: string;
  distanceLabel: string;
  isRecommended: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  modeBadgeBg: string;
  modeBadgeIconColor: string;
  ModeIcon: typeof FlightIcon;
};

export interface CapacityCheckResponse {
  feasible: boolean;
  qty: number;
  optimization_mode?: string;
  message?: string;
  banner?: {
    severity: "info" | "error" | "warning" | "success";
    message: string;
  } | null;
  max_possible_qty?: number;
  max_supplier_qty?: number;
  min_required_qty?: number;
  routes?: any[];
}

type SelectedScenarioPanelViewModel = {
  routePath: string;
  modeLabel: string;
  riskPct: number;
  riskColor: string;
  totalCostDisplay: string;
  shipmentCostDisplay: string;
  productCostDisplay: string;
  tariffCostDisplay: string | null;
  transitDays: number | string | null;
  distanceDisplay: string;
  estimatedArrival: string;
  whyRecommended: string;
  /** Lead time days clamped to [p10,p90] when all percentiles exist. */
  panelLeadTimeDisplayDays: number | null;
  /** "Mon D – Mon D, YYYY" delivery window from p10/p90 lead time, or null. */
  estimatedDeliveryRange: string | null;
  /** Single-date arrival using lead time display days when available, else base transit. */
  estimatedArrivalSingle: string;
  /** True when the route carries median/p10/p90 lead-time fields. */
  hasScorecardLead: boolean;
  /** Raw graph transit days prior to lead-time enrichment. */
  baseTransitDays: number | string | null;
};

export const hasRoutes = (routes?: any[]): routes is any[] =>
  Array.isArray(routes) && routes.length > 0;

export const getNoFeasibleSupplierMessage = (
  qtyValue: number,
  suppliers: FeasibleSupplier[],
  optimizationMode: string,
): React.ReactNode => {
  const modeLabel = OPTIMIZATION_MODE_LABELS[optimizationMode] || optimizationMode;
  if (!suppliers.length) {
    return <>No suppliers are available for the selected product and destination.</>;
  }

  const minQtyValues = suppliers
    .map((s) => s.supplier_min_qty)
    .filter((v): v is number => typeof v === "number");
  const maxQtyValues = suppliers.map((s) => s.supplier_capacity);

  const minRequiredQty = minQtyValues.length > 0 ? Math.min(...minQtyValues) : null;
  const maxAvailableQty = maxQtyValues.length > 0 ? Math.max(...maxQtyValues) : null;

  if (minRequiredQty != null && qtyValue < minRequiredQty) {
    return (
      <>
        Requested quantity is <strong>below the minimum quantity</strong> suppliers can
        supply for the selected product and destination. Minimum supplier quantity is{" "}
        <strong>{minRequiredQty.toLocaleString()} units</strong>. Please increase the
        quantity or continue to see top 5 suppliers optimized by <strong>{modeLabel}</strong>.
      </>
    );
  }

  if (maxAvailableQty != null && qtyValue > maxAvailableQty) {
    return (
      <>
        Requested quantity <strong>exceeds available supplier capacity</strong> for the
        selected product and destination. Maximum supplier quantity is{" "}
        <strong>{maxAvailableQty.toLocaleString()} units</strong>. Please adjust the
        quantity or continue to see top 5 suppliers optimized by <strong>{modeLabel}</strong>.
      </>
    );
  }

  return (
    <>
      Requested quantity <strong>does not fit within any supplier&apos;s minimum and
      maximum quantity limits</strong> for the selected product and destination. Please
      adjust the quantity or continue to see top 5 suppliers optimized by{" "}
      <strong>{modeLabel}</strong>.
    </>
  );
};

export const syncSelectedSupplier = (
  suppliers: FeasibleSupplier[],
  selectedSupplier: string,
  savedScenarioSupplier: string,
  setSupplier: React.Dispatch<React.SetStateAction<string>>,
  savedScenarioSupplierRef: React.MutableRefObject<string>,
) => {
  const supplierToRestore = savedScenarioSupplier || selectedSupplier;
  if (supplierToRestore && suppliers.some((s) => s.supplier_name === supplierToRestore)) {
    setSupplier(supplierToRestore);
    savedScenarioSupplierRef.current = "";
    return;
  }

  if (selectedSupplier && !suppliers.some((s) => s.supplier_name === selectedSupplier)) {
    setSupplier("");
  }
};

const MODE_META: Record<
  FreightMode,
  {
    label: string;
    badgeBg: string;
    iconColor: string;
    Icon: typeof FlightIcon;
  }
> = {
  air: {
    label: TRANSPORT_META.Air.label,
    badgeBg: TRANSPORT_META.Air.bgColor,
    iconColor: TRANSPORT_META.Air.color,
    Icon: TRANSPORT_META.Air.Icon,
  },
  truck: {
    label: TRANSPORT_META.Ground.label,
    badgeBg: TRANSPORT_META.Ground.bgColor,
    iconColor: TRANSPORT_META.Ground.color,
    Icon: TRANSPORT_META.Ground.Icon,
  },
  rail: {
    label: TRANSPORT_META.Rail.label,
    badgeBg: TRANSPORT_META.Rail.bgColor,
    iconColor: TRANSPORT_META.Rail.color,
    Icon: TRANSPORT_META.Rail.Icon,
  },
  sea: {
    label: TRANSPORT_META.Ocean.label,
    badgeBg: TRANSPORT_META.Ocean.bgColor,
    iconColor: TRANSPORT_META.Ocean.color,
    Icon: TRANSPORT_META.Ocean.Icon,
  },
};

const resolveFreightMode = (route: any): FreightMode => {
  const freightModes: string[] = route.freight_modes ?? [];
  const rawMode =
    (freightModes.length > 0 ? freightModes[0] : null) ??
    route.edges?.[0]?.freight_mode ??
    route.freight_mode ??
    "sea";
  const mode = rawMode.toLowerCase();
  if (mode.includes("air")) return "air";
  if (mode.includes("truck") || mode.includes("road") || mode.includes("ground")) return "truck";
  if (mode.includes("rail") || mode.includes("train")) return "rail";
  return "sea";
};

const isMultiModal = (route: any): boolean => {
  const freightModes: string[] = route.freight_modes ?? [];
  if (freightModes.length > 1) return true;
  const edges = route.edges ?? [];
  if (edges.length <= 1) return false;
  const modes = new Set(edges.map((e: any) => e.freight_mode).filter(Boolean));
  return modes.size > 1;
};

const formatRouteEndpoint = (
  nodeName: string | undefined,
  allNodeLookup: Map<string, LocationNode>,
) => {
  if (nodeName) {
    const nodeData = allNodeLookup.get(nodeName);
    return nodeData?.country ? `${nodeName}, ${nodeData.country}` : nodeName;
  }
  return "-";
};

const getModeDisplayLabel = (mode: string | undefined): string => {
  const normalized = String(mode ?? "").trim().toLowerCase();
  if (!normalized) return "Ocean";
  if (normalized.includes("air")) return "Air";
  if (normalized.includes("ground") || normalized.includes("truck") || normalized.includes("road")) return "Ground";
  if (normalized.includes("rail") || normalized.includes("train")) return "Rail";
  if (normalized.includes("sea") || normalized.includes("ocean") || normalized.includes("ship") || normalized.includes("vessel")) return "Ocean";
  return mode ?? "Ocean";
};

export const getRouteModeLabels = (route: any): string[] => {
  const freightModes: string[] = route.freight_modes ?? [];
  if (freightModes.length > 0) {
    return Array.from(new Set(freightModes.map(getModeDisplayLabel)));
  }

  const edgeModes = (route.edges ?? [])
    .map((edge: any) => getModeDisplayLabel(edge?.freight_mode ?? edge?.mode))
    .filter(Boolean);
  if (edgeModes.length > 0) {
    return Array.from(new Set(edgeModes));
  }

  return [getModeDisplayLabel(route.freight_mode)];
};

const formatRoutePath = (
  nodes: string[] | undefined,
  allNodeLookup: Map<string, LocationNode>,
) => {
  const routeNodes = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
  if (routeNodes.length === 0) return "-";
  return routeNodes
    .map((nodeName) => formatRouteEndpoint(nodeName, allNodeLookup))
    .join(" → ");
};

const getRouteQuantityValue = (route: any): number => {
  const qty = Number(route?.quantity ?? route?.qty);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
};

export const getShipmentCostValue = (route: any): number | null => {
  const shipmentCost = Number(route?.shipment_cost ?? route?.route_cost);
  return Number.isFinite(shipmentCost) ? shipmentCost : null;
};

export const getTariffCostValue = (route: any): number => {
  const tariffCost = Number(route?.tariff_cost);
  return Number.isFinite(tariffCost) && tariffCost > 0 ? tariffCost : 0;
};

export const getExtendedProductCostValue = (route: any): number | null => {
  const extendedProductCost = Number(route?.extended_product_cost);
  if (Number.isFinite(extendedProductCost)) return extendedProductCost;

  const unitCost = Number(route?.unit_cost_usd);
  if (Number.isFinite(unitCost)) {
    return unitCost * getRouteQuantityValue(route);
  }

  const productCost = Number(route?.product_cost);
  return Number.isFinite(productCost) ? productCost : null;
};

/* -------------------- Lead-time helpers -------------------- */

/**
 * Merge lightweight capacity-check route summaries back onto the full route
 * objects returned for mapping, preserving lead-time percentile fields.
 *
 * Spread order matters: `/get_route` attaches lead-time percentile fields and
 * may mirror median into total_time/time_days. `/route/capacity-check` returns
 * a lighter payload that still includes graph transit times — spreading it
 * after `route` would wipe lead time and show transit-only "Time" again.
 */
export const mergeCapacityRoutes = (capacityRoutes: any[], fetchedRoutes: any[]) => {
  const capLookup = new Map<string, any>();
  for (const route of capacityRoutes) {
    if (route.route_id) {
      capLookup.set(String(route.route_id), route);
    }
  }

  return fetchedRoutes.map((route: any) => {
    const capacityRoute = route.route_id
      ? capLookup.get(String(route.route_id))
      : undefined;
    if (!capacityRoute) return route;

    const merged = { ...route, ...capacityRoute, edges: route.edges };
    merged.median_lead_time_days = route.median_lead_time_days;
    merged.p10_lead_time_days = route.p10_lead_time_days;
    merged.p90_lead_time_days = route.p90_lead_time_days;
    const hasScorecardLead =
      route.median_lead_time_days != null ||
      route.p10_lead_time_days != null ||
      route.p90_lead_time_days != null;
    if (hasScorecardLead) {
      merged.total_time = route.total_time;
      merged.time_days = route.time_days;
    }
    return merged;
  });
};

/** Lead-time sort value, clamped to [p10,p90] around the median when available. */
export const getRouteLeadTimeSortValue = (route: any): number => {
  const productionDays = Number(route?.production_time_days);
  const neo4jTransitDays = Number(route?.neo4j_transit_time_days);
  const hasSplitTimes = Number.isFinite(productionDays) && Number.isFinite(neo4jTransitDays);
  const combinedRaw = hasSplitTimes ? productionDays + neo4jTransitDays : undefined;
  const transit = route?.total_time ?? route?.time_days;
  const medianRaw = combinedRaw ?? route?.median_lead_time_days ?? transit;
  if (medianRaw == null) return Number.POSITIVE_INFINITY;

  const p10 = route?.p10_lead_time_days;
  const p90 = route?.p90_lead_time_days;
  if (p10 == null || p90 == null) {
    const n = Number(medianRaw);
    return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
  }

  const lo = Number(p10);
  const hi = Number(p90);
  const mid = Number(medianRaw);
  if (Number.isNaN(lo) || Number.isNaN(hi) || Number.isNaN(mid)) {
    const n = Number(medianRaw);
    return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
  }

  return Math.min(Math.max(mid, lo), hi);
};

/** Integer lead-time display days (ceil of clamped sort value). */
export const getRouteLeadTimeDisplayDays = (route: any): number | null => {
  const sortVal = getRouteLeadTimeSortValue(route);
  if (!Number.isFinite(sortVal)) return null;
  return Math.ceil(sortVal);
};

/** Lead time days for the selected-scenario panel (clamped to [p10,p90] when all exist). */
export const getPanelLeadTimeDisplayDays = (panelRoute: any): number | null => {
  // Keep panel "Delivers in X days" aligned with route-card and sorting logic:
  // prefer production + Neo4j transit, then fallback to scorecard/transit fields.
  return getRouteLeadTimeDisplayDays(panelRoute);
};

/** "Mon D – Mon D, YYYY" when same year; otherwise full dates on both sides. */
export const getEstimatedDeliveryRangeText = (
  orderDate: string,
  panelRoute: any,
): string | null => {
  if (!orderDate) return null;
  const p10 = panelRoute?.p10_lead_time_days;
  const p90 = panelRoute?.p90_lead_time_days;
  if (p10 == null || p90 == null) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(orderDate);
  const base = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(orderDate);
  if (Number.isNaN(base.getTime())) return null;
  const p10Days = Math.ceil(Number(p10));
  const p90Days = Math.ceil(Number(p90));
  if (Number.isNaN(p10Days) || Number.isNaN(p90Days)) return null;
  const earliest = new Date(base.getTime() + p10Days * 24 * 60 * 60 * 1000);
  const latest = new Date(base.getTime() + p90Days * 24 * 60 * 60 * 1000);
  const fmtFull = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const fmtNoYear = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (earliest.getFullYear() === latest.getFullYear()) {
    return `${fmtNoYear(earliest)} \u2013 ${fmtNoYear(latest)}, ${latest.getFullYear()}`;
  }
  return `${fmtFull(earliest)} \u2013 ${fmtFull(latest)}`;
};

/** Optimization sort value. "time" uses lead-time display days. */
export const getRouteSortValue = (route: any, optimizationMode: string): number => {
  switch (optimizationMode) {
    case "cost":
      return Number(route.total_cost ?? route.route_cost ?? Number.POSITIVE_INFINITY);
    case "time":
      return getRouteLeadTimeDisplayDays(route) ?? Number.POSITIVE_INFINITY;
    case "distance":
      return Number(route.total_distance ?? route.distance_miles ?? Number.POSITIVE_INFINITY);
    case "risk": {
      let val: any = route.risk_score;
      if (val == null) {
        if (route.avg_prob_on_time == null) {
          val = route.prob_on_time == null ? Number.POSITIVE_INFINITY : 1 - route.prob_on_time;
        } else {
          val = 1 - route.avg_prob_on_time;
        }
      }
      return Number(val);
    }
    default:
      return Number(route.total_cost ?? route.route_cost ?? Number.POSITIVE_INFINITY);
  }
};

/** Sort a list of routes by the given optimization mode, with deterministic tiebreakers. */
export const sortRoutesForOptimization = (inputRoutes: any[], optimizationMode: string): any[] =>
  [...inputRoutes].sort((a, b) => {
    const primary = getRouteSortValue(a, optimizationMode) - getRouteSortValue(b, optimizationMode);
    if (primary !== 0) return primary;

    const totalCostA = Number(a.total_cost ?? a.route_cost ?? Number.POSITIVE_INFINITY);
    const totalCostB = Number(b.total_cost ?? b.route_cost ?? Number.POSITIVE_INFINITY);
    if (totalCostA !== totalCostB) return totalCostA - totalCostB;

    const timeA = getRouteLeadTimeDisplayDays(a) ?? Number.POSITIVE_INFINITY;
    const timeB = getRouteLeadTimeDisplayDays(b) ?? Number.POSITIVE_INFINITY;
    if (timeA !== timeB) return timeA - timeB;

    const distanceA = Number(a.total_distance ?? a.distance_miles ?? Number.POSITIVE_INFINITY);
    const distanceB = Number(b.total_distance ?? b.distance_miles ?? Number.POSITIVE_INFINITY);
    return distanceA - distanceB;
  });

export const getComputedTotalCostValue = (route: any): number | null => {
  const shipmentCost = getShipmentCostValue(route);
  const productCost = getExtendedProductCostValue(route);
  if (shipmentCost == null || productCost == null) {
    const totalCost = Number(route?.total_cost ?? route?.route_cost);
    return Number.isFinite(totalCost) ? totalCost : null;
  }

  return shipmentCost + productCost + getTariffCostValue(route);
};

const formatRouteCost = (route: any) => {
  const cost = getComputedTotalCostValue(route);
  return typeof cost === "number" ? `$${(cost / 1000).toFixed(1)}k` : "-";
};

const getRouteDistanceValue = (route: any): number | null => {
  const distance = Number(route?.total_distance ?? route?.distance_miles);
  return Number.isFinite(distance) ? distance : null;
};

const formatDistanceMiles = (value: number | null) => {
  if (value == null) return "—";
  return `${Math.round(value).toLocaleString()} Miles`;
};

const formatRouteTime = (route: any) => {
  const lead = getRouteLeadTimeDisplayDays(route);
  return lead == null ? "—" : `${lead} days`;
};

const formatRouteDistance = (route: any) => formatDistanceMiles(getRouteDistanceValue(route));

const getRiskColor = (riskPct: number) => {
  if (riskPct < 20) return "#16a34a";
  if (riskPct <= 40) return "#d97706";
  return "#dc2626";
};

const isPanelRouteSelected = (
  panelRoute: any,
  panelRouteIdx: number | null,
  route: any,
  idx: number,
) => {
  if (panelRoute == null) return false;
  return route.route_id == null
    ? panelRouteIdx === idx
    : panelRoute.route_id === route.route_id;
};

export const buildRouteCardViewModel = ({
  route,
  idx,
  allNodeLookup,
  panelRoute,
  panelRouteIdx,
  expandedRouteIds,
}: {
  route: any;
  idx: number;
  allNodeLookup: Map<string, LocationNode>;
  panelRoute: any;
  panelRouteIdx: number | null;
  expandedRouteIds: Set<number>;
}): RouteCardViewModel => {
  const nodes: string[] = route.nodes ?? [];
  const avgProb = route.avg_prob_on_time ?? route.prob_on_time ?? 0.5;
  const riskPct = Math.round((1 - avgProb) * 100);
  const mode = resolveFreightMode(route);
  const modeMeta = MODE_META[mode] ?? MODE_META.sea;
  const multiModal = isMultiModal(route);
  const modeLabels = getRouteModeLabels(route);

  let modeLabel: string;
  if (!multiModal) {
    modeLabel = modeMeta.label;
  } else if (modeLabels.length > 1) {
    modeLabel = modeLabels.join(" / ");
  } else {
    modeLabel = "Multi-Modal";
  }

  return {
    routePath: formatRoutePath(nodes, allNodeLookup),
    riskPct,
    riskColor: getRiskColor(riskPct),
    modeLabel,
    totalCost: formatRouteCost(route),
    timeLabel: formatRouteTime(route),
    distanceLabel: formatRouteDistance(route),
    isRecommended: idx === 0,
    isSelected: isPanelRouteSelected(panelRoute, panelRouteIdx, route, idx),
    isExpanded: expandedRouteIds.has(idx),
    modeBadgeBg: modeMeta.badgeBg,
    modeBadgeIconColor: modeMeta.iconColor,
    ModeIcon: modeMeta.Icon,
  };
};

const formatPanelCost = (value: unknown) =>
  typeof value === "number" ? `$${value.toLocaleString()}` : "—";

const getEstimatedArrival = (orderDate: string, transitDays: number | string | null) => {
  if (transitDays == null) return "—";

  const ymd = orderDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(orderDate) : null;
  let base: Date;
  if (ymd) {
    base = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  } else if (orderDate) {
    base = new Date(orderDate);
  } else {
    base = new Date();
  }
  const arrival = new Date(base.getTime() + Number(transitDays) * 24 * 60 * 60 * 1000);
  return arrival.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isFiniteMetric = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

const formatExplanationCost = (value: number) =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatExplanationDays = (value: number) => `${value} day${value === 1 ? "" : "s"}`;

const formatExplanationPct = (value: number) => `${Math.round(value * 100)}%`;

const formatExplanationMiles = (value: number) => `${value.toLocaleString()} miles`;

const getRouteTimeValue = (route: any): number | null => {
  // In route optimization, "Time" maps to lead-time display days.
  const leadDays = getRouteLeadTimeDisplayDays(route);
  return isFiniteMetric(leadDays) ? leadDays : null;
};

const getRouteOnTimeValue = (route: any): number | null => {
  let value: number | null = null;
  if (route.avg_prob_on_time != null) {
    value = Number(route.avg_prob_on_time);
  } else if (route.prob_on_time != null) {
    value = Number(route.prob_on_time);
  }
  return isFiniteMetric(value) ? value : null;
};

export const shouldDisplayRiskInsights = (route: any): boolean => {
  return getRouteOnTimeValue(route) != null;
};

const getExplanationDistanceValue = (route: any): number | null => {
  let value: number | null = null;
  if (route.total_distance != null) {
    value = Number(route.total_distance);
  } else if (route.distance_miles != null) {
    value = Number(route.distance_miles);
  }
  return isFiniteMetric(value) ? value : null;
};

const getValidMetricValues = (
  routes: any[],
  selector: (route: any) => number | null,
) => routes.map(selector).filter(isFiniteMetric);

const formatExplanationRouteText = (from: string, to: string) =>
  to ? `Route from ${from} to ${to}` : `Route ${from}`;

const buildComparisonSentence = ({
  currentValue,
  otherValues,
  pickComparisonValue,
  getDelta,
  buildWithDelta,
  buildWithoutDelta,
  buildStandalone,
}: {
  currentValue: number | null;
  otherValues: number[];
  pickComparisonValue: (values: number[]) => number;
  getDelta: (comparisonValue: number, current: number) => number;
  buildWithDelta: (current: number, delta: number) => string;
  buildWithoutDelta: (current: number) => string;
  buildStandalone: (current: number) => string;
}): string | null => {
  if (!isFiniteMetric(currentValue)) return null;
  if (otherValues.length === 0) return buildStandalone(currentValue);

  const comparisonValue = pickComparisonValue(otherValues);
  const delta = getDelta(comparisonValue, currentValue);
  return delta > 0
    ? buildWithDelta(currentValue, delta)
    : buildWithoutDelta(currentValue);
};

const buildCostExplanation = (
  currentCost: number | null,
  otherCosts: number[],
  currentTime: number | null,
  currentOnTime: number | null,
  from: string,
  to: string,
) => {
  const winSentence = buildComparisonSentence({
    currentValue: currentCost,
    otherValues: otherCosts,
    pickComparisonValue: (values) => Math.min(...values),
    getDelta: (nextBest, current) => nextBest - current,
    buildWithDelta: (current, delta) =>
      `Selected because it has the lowest total cost at ${formatExplanationCost(current)}, which is ${formatExplanationCost(delta)} less than the next visible option.`,
    buildWithoutDelta: (current) =>
      `Selected because it has the lowest total cost at ${formatExplanationCost(current)}.`,
    buildStandalone: (current) =>
      `${formatExplanationRouteText(from, to)} with a total cost of ${formatExplanationCost(current)}.`,
  });

  const parts: string[] = [];
  if (isFiniteMetric(currentTime)) parts.push(`Lead time is ${formatExplanationDays(currentTime)}`);
  if (isFiniteMetric(currentOnTime)) parts.push(`${formatExplanationPct(currentOnTime)} on-time performance`);

  return {
    winSentence,
    tradeoffSentence: parts.length > 0 ? ` ${parts.join(" with ")}.` : "",
  };
};

const buildTimeExplanation = (
  currentTime: number | null,
  otherTimes: number[],
  currentCost: number | null,
  from: string,
  to: string,
) => ({
  winSentence: buildComparisonSentence({
    currentValue: currentTime,
    otherValues: otherTimes,
    pickComparisonValue: (values) => Math.min(...values),
    getDelta: (nextBest, current) => nextBest - current,
    buildWithDelta: (current, delta) =>
      `Selected because it is the fastest visible route at ${formatExplanationDays(current)}, ${formatExplanationDays(delta)} quicker than the next option.`,
    buildWithoutDelta: (current) =>
      `Selected because it is the fastest visible route at ${formatExplanationDays(current)}.`,
    buildStandalone: (current) =>
      `${formatExplanationRouteText(from, to)} with a lead time of ${formatExplanationDays(current)}.`,
  }),
  tradeoffSentence: isFiniteMetric(currentCost)
    ? ` Total cost is ${formatExplanationCost(currentCost)}.`
    : "",
});

const buildRiskExplanation = (
  currentOnTime: number | null,
  otherOnTimes: number[],
  currentTime: number | null,
  from: string,
  to: string,
) => ({
  winSentence: buildComparisonSentence({
    currentValue: currentOnTime,
    otherValues: otherOnTimes,
    pickComparisonValue: (values) => Math.max(...values),
    getDelta: (nextBest, current) => Math.round((current - nextBest) * 100),
    buildWithDelta: (current, delta) =>
      `Selected because it has the strongest reliability at ${formatExplanationPct(current)} on-time performance, ${delta} point${delta === 1 ? "" : "s"} higher than the next option.`,
    buildWithoutDelta: (current) =>
      `Selected because it has the strongest reliability at ${formatExplanationPct(current)} on-time performance.`,
    buildStandalone: (current) =>
      `${formatExplanationRouteText(from, to)} with ${formatExplanationPct(current)} on-time performance.`,
  }),
  tradeoffSentence: isFiniteMetric(currentTime)
    ? ` Lead time is ${formatExplanationDays(currentTime)}.`
    : "",
});

const buildDistanceExplanation = (
  currentDistance: number | null,
  otherDistances: number[],
  currentCost: number | null,
  from: string,
  to: string,
) => ({
  winSentence: buildComparisonSentence({
    currentValue: currentDistance,
    otherValues: otherDistances,
    pickComparisonValue: (values) => Math.min(...values),
    getDelta: (nextBest, current) => nextBest - current,
    buildWithDelta: (current, delta) =>
      `Selected because it is the shortest visible route at ${formatExplanationMiles(current)}, ${formatExplanationMiles(delta)} shorter than the next option.`,
    buildWithoutDelta: (current) =>
      `Selected because it is the shortest visible route at ${formatExplanationMiles(current)}.`,
    buildStandalone: (current) =>
      `${formatExplanationRouteText(from, to)} spanning ${formatExplanationMiles(current)}.`,
  }),
  tradeoffSentence: isFiniteMetric(currentCost)
    ? ` Total cost is ${formatExplanationCost(currentCost)}.`
    : "",
});

const buildFallbackExplanation = (
  currentCost: number | null,
  currentTime: number | null,
  from: string,
  to: string,
) => {
  const parts: string[] = [];
  if (isFiniteMetric(currentCost)) parts.push(`total cost ${formatExplanationCost(currentCost)}`);
  if (isFiniteMetric(currentTime)) parts.push(`lead time ${formatExplanationDays(currentTime)}`);

  return parts.length > 0
    ? `${formatExplanationRouteText(from, to)} with ${parts.join(" and ")}.`
    : `${formatExplanationRouteText(from, to)}.`;
};

export const buildRouteExplanation = ({
  route,
  visibleRoutes,
  optimization,
  from,
  to,
}: {
  route: any;
  visibleRoutes: any[];
  optimization: string;
  from: string;
  to: string;
}): string => {
  const others = visibleRoutes.filter((r) => r !== route);
  const currentCost = getComputedTotalCostValue(route);
  const currentTime = getRouteTimeValue(route);
  const currentOnTime = getRouteOnTimeValue(route);
  const currentDistance = getExplanationDistanceValue(route);

  const explanationByOptimization: Record<string, () => { winSentence: string | null; tradeoffSentence: string }> = {
    cost: () => buildCostExplanation(
      currentCost,
      getValidMetricValues(others, getComputedTotalCostValue),
      currentTime,
      currentOnTime,
      from,
      to,
    ),
    time: () => buildTimeExplanation(
      currentTime,
      getValidMetricValues(others, getRouteTimeValue),
      currentCost,
      from,
      to,
    ),
    risk: () => buildRiskExplanation(
      currentOnTime,
      getValidMetricValues(others, getRouteOnTimeValue),
      currentTime,
      from,
      to,
    ),
    distance: () => buildDistanceExplanation(
      currentDistance,
      getValidMetricValues(others, getExplanationDistanceValue),
      currentCost,
      from,
      to,
    ),
  };

  const explanation = explanationByOptimization[optimization]?.();
  if (explanation?.winSentence) {
    return explanation.winSentence + explanation.tradeoffSentence;
  }

  return buildFallbackExplanation(currentCost, currentTime, from, to);
};

export const buildSelectedScenarioPanelViewModel = ({
  panelRoute,
  panelRouteIdx,
  allNodeLookup,
  optimization,
  orderDate,
  visibleRoutes,
}: {
  panelRoute: any;
  panelRouteIdx: number;
  allNodeLookup: Map<string, LocationNode>;
  optimization: string;
  orderDate: string;
  visibleRoutes: any[];
}): SelectedScenarioPanelViewModel => {
  const nodes: string[] = panelRoute.nodes ?? [];
  const avgProb = panelRoute.avg_prob_on_time ?? panelRoute.prob_on_time ?? 0.5;
  const riskPct = Math.round((1 - avgProb) * 100);
  const panelModeLabels = getRouteModeLabels(panelRoute);
  let modeLabel: string;
  if (!isMultiModal(panelRoute)) {
    modeLabel = MODE_META[resolveFreightMode(panelRoute)]?.label ?? MODE_META.sea.label;
  } else if (panelModeLabels.length > 1) {
    modeLabel = panelModeLabels.join(" / ");
  } else {
    modeLabel = "Multi-Modal";
  }
  const routePath = formatRoutePath(nodes, allNodeLookup).replace("-", "—");
  const baseTransitDays =
    panelRoute.neo4j_transit_time_days ?? panelRoute.total_time ?? panelRoute.time_days ?? null;
  const panelLeadTimeDisplayDays = getPanelLeadTimeDisplayDays(panelRoute);
  const estimatedDeliveryRange = getEstimatedDeliveryRangeText(orderDate, panelRoute);
  const hasScorecardLead =
    panelRoute.median_lead_time_days != null
    || (panelRoute.p10_lead_time_days != null && panelRoute.p90_lead_time_days != null);
  const transitDays = panelLeadTimeDisplayDays ?? baseTransitDays;
  const shipmentCost = getShipmentCostValue(panelRoute);
  const displayProductCost = getExtendedProductCostValue(panelRoute);
  const hasTariffResult = panelRoute?.tariff_cost != null;
  const estimatedArrivalSingle = getEstimatedArrival(
    orderDate,
    panelLeadTimeDisplayDays ?? baseTransitDays,
  );
  return {
    routePath,
    modeLabel,
    riskPct,
    riskColor: getRiskColor(riskPct),
    totalCostDisplay: formatPanelCost(getComputedTotalCostValue(panelRoute)),
    shipmentCostDisplay: formatPanelCost(shipmentCost),
    productCostDisplay: formatPanelCost(displayProductCost),
    tariffCostDisplay: hasTariffResult
      ? formatPanelCost(getTariffCostValue(panelRoute))
      : null,
    transitDays,
    distanceDisplay: formatRouteDistance(panelRoute),
    estimatedArrival: estimatedArrivalSingle,
    whyRecommended: buildRouteExplanation({
      route: panelRoute,
      visibleRoutes,
      optimization,
      from: routePath,
      to: "",
    }),
    panelLeadTimeDisplayDays,
    estimatedDeliveryRange,
    estimatedArrivalSingle,
    hasScorecardLead,
    baseTransitDays,
  };
};

const formatScenarioCurrency = (value: unknown): string | undefined => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return undefined;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export const buildSavedScenario = (params: {
  product: string;
  destination: string;
  qty: string;
  supplier: string;
  optimization: string;
  route?: any;
}): SavedScenario => {
  const nodes = Array.isArray(params.route?.nodes) ? params.route.nodes : [];
  const riskScore = Number(params.route?.risk_score);
  return {
    id: `scenario-${crypto.randomUUID()}`,
    product: params.product,
    destination: params.destination,
    quantity: params.qty,
    supplier: params.supplier,
    strategy: params.optimization,
    savedAt: new Date().toISOString(),
    routeName: params.route?.route_name ? String(params.route.route_name) : undefined,
    from: nodes.length > 0 ? String(nodes[0]) : undefined,
    to: nodes.length > 1 ? String(nodes[nodes.length - 1]) : undefined,
    totalCost: formatScenarioCurrency(params.route?.total_cost ?? params.route?.route_cost),
    riskPct: Number.isFinite(riskScore) ? Math.round(riskScore * 100) : undefined,
    riskColor: Number.isFinite(riskScore) ? "#d97706" : undefined,
    tariffImpact: null,
    mode: params.route?.freight_mode ? String(params.route.freight_mode) : undefined,
    modeLabel: params.route?.freight_mode ? String(params.route.freight_mode) : undefined,
    eta: params.route?.eta ? String(params.route.eta) : undefined,
  };
};

